import { Briefcase, Calendar, MapPin, Search } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import Spinner from '../../components/Spinner';
import { usePublicJobBoard, usePublicJobs } from '../../hooks/useJobBoard';
import { JobDto } from '../../types/jobBoard';
import { useMemo, useState } from 'react';
import Select from "react-select";
import type { MultiValue } from "react-select";

type SelectOption = {
  value: string;
  label: string;
};

const date = (value?: string) => value ? new Date(value).toLocaleDateString() : 'Not specified';
const name = (
  job: JobDto,
  field: 'industry' | 'country'
) =>
  field === 'industry'
    ? job.industryNames?.join(', ') || 'Industry not specified'
    : job.countryNames?.join(', ') || 'Country not specified';
export default function PublicJobBoardPage() {
  const { slug = '' } = useParams();
  const settings = usePublicJobBoard(slug);
  const jobs = usePublicJobs(slug);
  const colors = settings.data;
  const loading = settings.isLoading || jobs.isLoading;

  const industries = [
    ...new Set(
      jobs.data?.flatMap(
        j => j.industryNames ?? []
      ) ?? []
    )
  ];

  const countries = [
    ...new Set(
      jobs.data?.flatMap(
        j => j.countryNames ?? []
      ) ?? []
    )
  ];

  const [search, setSearch] = useState('');
  const [industriesFilter, setIndustriesFilter] = useState<string[]>([]);
  const [countriesFilter, setCountriesFilter] = useState<string[]>([]);

  const filteredJobs = useMemo(() => {
    return (
      jobs.data
        ?.filter(job => {
          const searchMatch =
            !search ||
            job.title
              .toLowerCase()
              .includes(search.toLowerCase()) ||
            job.description
              .toLowerCase()
              .includes(search.toLowerCase()) ||
            (job.industryNames ?? []).some(x =>
              x.toLowerCase().includes(search.toLowerCase())
            ) ||
            (job.countryNames ?? []).some(x =>
              x.toLowerCase().includes(search.toLowerCase())
            );

          const industryMatch =
            !industriesFilter.length ||
            job.industryNames?.some(ind =>
              industriesFilter.includes(ind)
            );

          const countryMatch =
            !countriesFilter.length ||
            job.countryNames?.some(cn =>
              countriesFilter.includes(cn)
            );

          return (
            searchMatch &&
            industryMatch &&
            countryMatch
          );
        })
        .sort(
          (a, b) =>
            new Date(b.publishedDate ?? '').getTime() -
            new Date(a.publishedDate ?? '').getTime()
        ) ?? []
    );
  }, [jobs.data, search, industriesFilter, countriesFilter]);

  if (loading) return <div className="flex min-h-screen items-center justify-center"><Spinner /></div>;
  if (settings.isError || jobs.isError) return <div className="mx-auto flex min-h-screen max-w-xl flex-col items-center justify-center p-6 text-center"><Briefcase size={44} className="text-slate-300" /><h1 className="mt-4 text-2xl font-bold text-slate-900">Job board unavailable</h1><p className="mt-2 text-slate-500">This job board may be private, expired, or temporarily unavailable.</p></div>;
  
  const selectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: "48px",
      borderRadius: "0.75rem",
      borderColor: state.isFocused
        ? "var(--job-accent)"
        : "#e2e8f0",
      boxShadow: "none",
      backgroundColor: "#fff",
      "&:hover": {
        borderColor: "var(--job-accent)",
      },
    }),

    menu: (base: any) => ({
      ...base,
      backgroundColor: "#fff",
      zIndex: 9999,
    }),

    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "var(--job-primary)"
        : state.isFocused
        ? "#f8fafc"
        : "#fff",
      color: state.isSelected
        ? "#fff"
        : "#1e293b",
      cursor: "pointer",
    }),

    placeholder: (base: any) => ({
      ...base,
      color: "#94a3b8",
    }),

    singleValue: (base: any) => ({
      ...base,
      color: "var(--job-primary)",
    }),

    indicatorSeparator: () => ({
      display: "none",
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: "rgba(225,29,72,.12)",
    }),

    multiValueLabel: (base: any) => ({
      ...base,
      color: "var(--job-accent)",
      fontWeight: 600,
    }),

    multiValueRemove: (base: any) => ({
      ...base,
      color: "var(--job-accent)",
      ":hover": {
        backgroundColor: "var(--job-accent)",
        color: "#fff",
      },
    }),
  };
  
  return (
    <div
      className="w-full min-h-screen"
      style={
        {
          "--job-primary": colors?.primaryColor ?? "#172554",
          "--job-secondary": colors?.secondaryColor ?? "#e2e8f0",
          "--job-button": colors?.buttonColor ?? "#7f1d1d",
          "--job-accent": colors?.accentColor ?? "#be123c",
          backgroundColor: "var(--job-secondary)",
        } as React.CSSProperties
      }
    >
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto flex flex-col items-center gap-0 px-4 py-4 text-center md:flex-row md:items-center md:gap-2 md:px-6 md:text-left">
          {colors?.logoUrl ? (
            <img
              src={colors.logoUrl}
              alt="Company Logo"
              className="h-16 w-full max-w-[200px] object-contain md:h-16 md:max-w-[200px]"
            />
          ) : (
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-[var(--job-primary)] text-white">
              <Briefcase size={26} />
            </div>
          )}

          <div className="text-center md:text-left">
            <p className="text-sm font-semibold uppercase tracking-[0.16em]" style={{ color: "var(--job-accent)" }}>
              Careers
            </p>

            <h1 className="text-center text-2xl font-bold leading-none md:text-left md:text-3xl" style={{ color: "var(--job-primary)" }}>
              {colors?.companyName ??
                colors?.jobBoardSlug ??
                "Job Board"}
            </h1>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-10">
        <section className="mb-10 overflow-hidden rounded-3xl bg-[var(--job-primary)] px-6 py-10 text-center shadow-lg md:px-12 md:py-14">
          <h2 className="mt-3 text-3xl font-bold md:text-5xl text-white">
            Build Your Future With Us
          </h2>

          <p className="mx-auto mt-4 max-w-2xl text-base text-white/80">
            Join a team focused on growth, innovation, and delivering
            exceptional solutions. Explore our open opportunities and
            find the role that's right for you.
          </p>

          <div className="mt-6 inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur-sm text-white">
            {filteredJobs.length} Open Positions
          </div>
        </section>

        {/* <div className="mb-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <p className="mt-2 text-3xl font-bold" style={{color: "var(--job-primary)"}}>
              Opportunities
            </p>

            <p className="mt-2 text-3xl font-bold" style={{color: "var(--job-primary)"}}>
              {jobs.data?.length ?? 0}
            </p>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <p className="mt-2 text-3xl font-bold" style={{color: "var(--job-primary)"}}>
              Industries
            </p>

            <p className="mt-2 text-3xl font-bold" style={{color: "var(--job-primary)"}}>
              {
                new Set(
                  jobs.data?.map(
                    (job) =>
                      job.industryName ??
                      job.industryId
                  )
                ).size
              }
            </p>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <p className="mt-2 text-3xl font-bold" style={{color: "var(--job-primary)"}}>
              Locations
            </p>

            <p className="mt-2 text-3xl font-bold" style={{color: "var(--job-primary)"}}>
              {
                new Set(
                  jobs.data?.map(
                    (job) =>
                      job.countryName ??
                      job.countryId
                  )
                ).size
              }
            </p>
          </div>
        </div> */}

        <div className="mb-8 rounded-2xl bg-white p-4 shadow-sm">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="relative">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                type="text"
                placeholder="Search jobs..."
                value={search}
                onChange={(e) =>
                  setSearch(e.target.value)
                }
                className="w-full rounded-xl border border-slate-200 py-3 pl-10 pr-4 outline-none focus:ring-2" 
                style={{
                  color: "var(--job-primary)",
                  borderColor:
                    search.length > 0
                      ? 'var(--job-accent)'
                      : undefined,
                }}
                
              />
            </div>

            <Select<SelectOption, true>
              isMulti
              closeMenuOnSelect={false}
              placeholder="Filter industries..."
              options={industries.map((item) => ({
                value: item,
                label: item,
              }))}
              value={industriesFilter.map((item) => ({
                value: item,
                label: item,
              }))}
              onChange={(
                options: MultiValue<SelectOption>
              ) =>
                setIndustriesFilter(
                  options.map((x) => x.value)
                )
              }
              styles={selectStyles}
            />

            <Select<SelectOption, true>
              isMulti
              closeMenuOnSelect={false}
              placeholder="Filter locations..."
              options={countries.map((item) => ({
                value: item,
                label: item,
              }))}
              value={countriesFilter.map((item) => ({
                value: item,
                label: item,
              }))}
              onChange={(
                options: MultiValue<SelectOption>
              ) =>
                setCountriesFilter(
                  options.map((x) => x.value)
                )
              }
              styles={selectStyles}
            />
          </div>
        </div>

        {!filteredJobs.length ? (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-14 text-center">
            <Briefcase
              className="mx-auto text-slate-300"
              size={48}
            />

            <h3 className="mt-4 text-xl font-bold text-slate-900">
              No Matching Jobs Found
            </h3>

            <p className="mt-2 text-slate-500">
              Try adjusting your search criteria or filters.
            </p>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-2">
            {filteredJobs.map((job) => (
              <Link
                key={job.jobId}
                to={`/jobs/${slug}/${job.jobId}`}
                className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-[var(--job-accent)] hover:shadow-xl"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <h3 className="break-words text-lg font-bold transition-colors hover:opacity-80 md:text-xl" style={{ color: "var(--job-primary)" }}>
                    {job.title}
                  </h3>

                  <span className="rounded-xl px-3 py-1 text-xs font-semibold text-white" style={{ backgroundColor: "var(--job-primary)" }}>
                    Open
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="rounded-full px-3 py-1 text-xs font-semibold" style={{backgroundColor: `${colors?.accentColor ?? "#e11d48"}20`, color: "var(--job-accent)"}} >
                    {name(job, "industry")}
                  </span>

                  <span className="rounded-full px-3 py-1 text-xs font-semibold" style={{backgroundColor: `${colors?.accentColor ?? "#e11d48"}20`, color: "var(--job-accent)"}} >
                    {name(job, "country")}
                  </span>
                </div>

                <p className="mt-4 line-clamp-3 text-sm leading-6 text-slate-600">
                  {job.description}
                </p>

                <div className="mt-5 grid gap-2 text-sm text-slate-500 sm:flex sm:flex-wrap sm:gap-x-5">
                  <span className="flex items-center gap-1.5">
                    <Briefcase size={15} />
                    {name(job, "industry")}
                  </span>

                  <span className="flex items-center gap-1.5">
                    <MapPin size={15} />
                    {name(job, "country")}
                  </span>

                  <span className="flex items-center gap-1.5">
                    <Calendar size={15} />
                    Published {date(job.publishedDate)}
                  </span>
                </div>

                <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
                  <span
                    className="rounded-xl px-5 py-2 text-center text-sm font-semibold text-white transition-transform group-hover:scale-105"
                    style={{
                      backgroundColor: "var(--job-button)"
                    }}
                  >
                    View Details
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}